import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Clock, AlertTriangle, ChevronRight, Calendar, Stethoscope, FileText } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TourenplanungPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => { loadTour(); }, [date]);

  const loadTour = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('wunddok_token');
      const res = await axios.get(`${API}/api/tourenplanung?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const formatDate = d => { try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }); } catch { return d; } };

  const getPriorityBadge = (item) => {
    if (item.infektionszeichen?.length > 0) return { label: 'Kritisch', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    if (item.is_overdue) return { label: 'Überfällig', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    if (item.is_due) return { label: 'Heute fällig', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    if (item.evaluation_status === 'verschlechtert') return { label: 'Verschlechtert', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    return { label: 'Geplant', cls: 'bg-slate-100 text-slate-500 border-slate-200' };
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4" data-testid="tourenplanung-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 tracking-tight">Tourenplanung</h1>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-40 h-10" data-testid="tour-date-input" />
      </div>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="border border-slate-200 shadow-sm" data-testid="tour-stat-total">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-900">{data.total}</p>
              <p className="text-xs text-slate-500">Gesamt</p>
            </CardContent>
          </Card>
          <Card className="border border-rose-200 shadow-sm" data-testid="tour-stat-overdue">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-rose-600">{data.overdue}</p>
              <p className="text-xs text-slate-500">Überfällig</p>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 shadow-sm" data-testid="tour-stat-due">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{data.due_today}</p>
              <p className="text-xs text-slate-500">Heute fällig</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tour Items */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
      ) : data?.items?.length > 0 ? (
        <div className="space-y-2">
          {data.items.map((item, idx) => {
            const badge = getPriorityBadge(item);
            return (
              <Card key={`${item.wound_id}-${idx}`} className={`border shadow-sm hover:shadow-md transition-all cursor-pointer ${item.is_overdue ? 'border-rose-200' : item.is_due ? 'border-amber-200' : 'border-slate-200'}`}
                onClick={() => navigate(`/wunde/${item.wound_id}`)} data-testid={`tour-item-${item.wound_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${item.is_overdue ? 'bg-rose-50' : item.is_due ? 'bg-amber-50' : 'bg-slate-100'}`}>
                      {item.is_overdue ? <AlertTriangle className="w-5 h-5 text-rose-600" /> :
                        item.is_due ? <Clock className="w-5 h-5 text-amber-600" /> :
                          <Stethoscope className="w-5 h-5 text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 truncate">{item.patient_name}</p>
                        <Badge variant="outline" className={`text-[10px] border shrink-0 ${badge.cls}`}>{badge.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{item.wundart} &middot; {item.lokalisation}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                        {item.naechster_vw && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />VW: {formatDate(item.naechster_vw)}</span>}
                        {item.vw_frequenz && <span>{item.vw_frequenz}</span>}
                        <span>Fläche: {item.last_flaeche} cm²</span>
                      </div>
                      {item.patient_adresse && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{item.patient_adresse}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); navigate(`/wunde/${item.wound_id}/neuer-bericht`); }}
                        data-testid={`tour-report-${item.wound_id}`}>
                        <FileText className="w-3 h-3 mr-1" />Bericht
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">Keine Verbandwechsel geplant</p>
          <p className="text-sm mt-1">Für diesen Tag sind keine Termine eingetragen</p>
        </div>
      )}
    </div>
  );
}
